"use client"

import {
  createContext,
  isValidElement,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react"
import { cjk } from "@streamdown/cjk"
import { code } from "@streamdown/code"
import { math } from "@streamdown/math"
import { mermaid } from "@streamdown/mermaid"
import { ButtonGroup, ButtonGroupText } from "@ui/button-group"
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import type { UIMessage } from "ai"
import { ChevronLeftIcon, ChevronRightIcon, FileIcon } from "lucide-react"
import { bundledLanguagesInfo, type BundledLanguage } from "shiki"
import { Streamdown, type Components } from "streamdown"

import {
  CodeBlock,
  CodeBlockActions,
  CodeBlockCopyButton,
  CodeBlockFilename,
  CodeBlockHeader,
  CodeBlockLanguageSelector,
  CodeBlockLanguageSelectorContent,
  CodeBlockLanguageSelectorItem,
  CodeBlockLanguageSelectorTrigger,
  CodeBlockLanguageSelectorValue,
  CodeBlockTitle,
  type CodeBlockLanguage,
} from "./code-block"

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"]
}

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      "group flex w-full flex-col gap-2",
      from === "user" ? "is-user ml-auto max-w-[75%] justify-end" : "is-assistant max-w-full",
      className
    )}
    {...props}
  />
)

export type MessageContentProps = HTMLAttributes<HTMLDivElement>

export const MessageContent = ({ children, className, ...props }: MessageContentProps) => (
  <div
    className={cn(
      "is-user:dark flex w-fit max-w-full min-w-0 flex-col gap-2 text-sm group-[.is-assistant]:w-full",
      "group-[.is-user]:bg-sand-200 group-[.is-user]:text-sand-950 group-[.is-user]:ml-auto group-[.is-user]:overflow-hidden group-[.is-user]:rounded-lg group-[.is-user]:px-2.5 group-[.is-user]:py-1.5",
      "group-[.is-assistant]:text-foreground",
      className
    )}
    {...props}
  >
    {children}
  </div>
)

export type MessageActionsProps = ComponentProps<"div">

export const MessageActions = ({ className, children, ...props }: MessageActionsProps) => (
  <div className={cn("flex items-center gap-1", className)} {...props}>
    {children}
  </div>
)

export type MessageActionProps = ComponentProps<typeof Button> & {
  tooltip?: string
  label?: string
}

export const MessageAction = ({
  tooltip,
  children,
  label,
  variant = "ghost",
  size = "iconSm",
  ...props
}: MessageActionProps) => {
  const button = (
    <Button size={size} type="button" variant={variant} {...props}>
      {children}
      <span className="sr-only">{label || tooltip}</span>
    </Button>
  )

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return button
}

interface MessageBranchContextType {
  currentBranch: number
  totalBranches: number
  goToPrevious: () => void
  goToNext: () => void
  branches: ReactElement[]
  setBranches: (branches: ReactElement[]) => void
}

const MessageBranchContext = createContext<MessageBranchContextType | null>(null)

const useMessageBranch = () => {
  const context = useContext(MessageBranchContext)

  if (!context) {
    throw new Error("MessageBranch components must be used within MessageBranch")
  }

  return context
}

export type MessageBranchProps = HTMLAttributes<HTMLDivElement> & {
  defaultBranch?: number
  onBranchChange?: (branchIndex: number) => void
}

export const MessageBranch = ({ defaultBranch = 0, onBranchChange, className, ...props }: MessageBranchProps) => {
  const [currentBranch, setCurrentBranch] = useState(defaultBranch)
  const [branches, setBranches] = useState<ReactElement[]>([])

  const handleBranchChange = useCallback(
    (newBranch: number) => {
      setCurrentBranch(newBranch)
      onBranchChange?.(newBranch)
    },
    [onBranchChange]
  )

  const goToPrevious = useCallback(() => {
    const newBranch = currentBranch > 0 ? currentBranch - 1 : branches.length - 1
    handleBranchChange(newBranch)
  }, [currentBranch, branches.length, handleBranchChange])

  const goToNext = useCallback(() => {
    const newBranch = currentBranch < branches.length - 1 ? currentBranch + 1 : 0
    handleBranchChange(newBranch)
  }, [currentBranch, branches.length, handleBranchChange])

  const contextValue = useMemo<MessageBranchContextType>(
    () => ({
      branches,
      currentBranch,
      goToNext,
      goToPrevious,
      setBranches,
      totalBranches: branches.length,
    }),
    [branches, currentBranch, goToNext, goToPrevious]
  )

  return (
    <MessageBranchContext.Provider value={contextValue}>
      <div className={cn("grid w-full gap-2 [&>div]:pb-0", className)} {...props} />
    </MessageBranchContext.Provider>
  )
}

export type MessageBranchContentProps = HTMLAttributes<HTMLDivElement>

export const MessageBranchContent = ({ children, ...props }: MessageBranchContentProps) => {
  const { currentBranch, setBranches, branches } = useMessageBranch()
  const childrenArray = useMemo(() => (Array.isArray(children) ? children : [children]), [children])

  // Use useEffect to update branches when they change
  useEffect(() => {
    if (branches.length !== childrenArray.length) {
      setBranches(childrenArray)
    }
  }, [childrenArray, branches, setBranches])

  return childrenArray.map((branch, index) => (
    <div
      className={cn("grid gap-2 overflow-hidden [&>div]:pb-0", index === currentBranch ? "block" : "hidden")}
      key={branch.key}
      {...props}
    >
      {branch}
    </div>
  ))
}

export type MessageBranchSelectorProps = ComponentProps<typeof ButtonGroup>

export const MessageBranchSelector = ({ className, ...props }: MessageBranchSelectorProps) => {
  const { totalBranches } = useMessageBranch()

  // Don't render if there's only one branch
  if (totalBranches <= 1) {
    return null
  }

  return (
    <ButtonGroup
      className={cn("[&>*:not(:first-child)]:rounded-l-md [&>*:not(:last-child)]:rounded-r-md", className)}
      orientation="horizontal"
      {...props}
    />
  )
}

export type MessageBranchPreviousProps = ComponentProps<typeof Button>

export const MessageBranchPrevious = ({ children, ...props }: MessageBranchPreviousProps) => {
  const { goToPrevious, totalBranches } = useMessageBranch()

  return (
    <Button
      aria-label="Previous branch"
      disabled={totalBranches <= 1}
      onClick={goToPrevious}
      size="iconSm"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <ChevronLeftIcon size={14} />}
    </Button>
  )
}

export type MessageBranchNextProps = ComponentProps<typeof Button>

export const MessageBranchNext = ({ children, ...props }: MessageBranchNextProps) => {
  const { goToNext, totalBranches } = useMessageBranch()

  return (
    <Button
      aria-label="Next branch"
      disabled={totalBranches <= 1}
      onClick={goToNext}
      size="iconSm"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <ChevronRightIcon size={14} />}
    </Button>
  )
}

export type MessageBranchPageProps = HTMLAttributes<HTMLSpanElement>

export const MessageBranchPage = ({ className, ...props }: MessageBranchPageProps) => {
  const { currentBranch, totalBranches } = useMessageBranch()

  return (
    <ButtonGroupText
      className={cn("text-muted-foreground border-none bg-transparent shadow-none", className)}
      {...props}
    >
      {currentBranch + 1} of {totalBranches}
    </ButtonGroupText>
  )
}

export type MessageResponseProps = ComponentProps<typeof Streamdown>

const streamdownPlugins = { cjk, code, math, mermaid }

type MarkdownCodeProps = ComponentProps<"code"> & {
  "data-block"?: boolean | string
  node?: {
    properties?: {
      metastring?: string
    }
  }
}

type CodeLanguageInfo = {
  id: BundledLanguage
  name: string
  aliases?: string[]
}

const plainTextLanguages = new Set(["text", "txt", "plain", "plaintext"])

const codeLanguageInfoByAlias = new Map<string, CodeLanguageInfo>()
const bundledLanguageIds = new Set<string>(bundledLanguagesInfo.map((language) => language.id))

function isBundledLanguageId(language: string): language is BundledLanguage {
  return bundledLanguageIds.has(language)
}

for (const language of bundledLanguagesInfo) {
  if (!isBundledLanguageId(language.id)) continue

  const languageInfo: CodeLanguageInfo = {
    aliases: language.aliases ? [...language.aliases] : undefined,
    id: language.id,
    name: language.name,
  }

  codeLanguageInfoByAlias.set(language.id, languageInfo)
  language.aliases?.forEach((alias) => codeLanguageInfoByAlias.set(alias, languageInfo))
}

const defaultMarkdownComponents: Components = {
  code: MarkdownCode,
}

function MarkdownCode({ children, className, node, "data-block": dataBlock, ...props }: MarkdownCodeProps) {
  if (!dataBlock) {
    return (
      <code className={cn("bg-muted rounded px-1.5 py-0.5 font-mono text-sm", className)} {...props}>
        {children}
      </code>
    )
  }

  const codeText = trimTrailingNewlines(textFromReactNode(children))
  const filename = codeFilenameFromMeta(node?.properties?.metastring)
  const classLanguage = codeLanguageFromClassName(className)
  const filenameLanguage = filename ? codeLanguageFromFilename(filename) : undefined
  const languageInput =
    classLanguage && !plainTextLanguages.has(classLanguage) ? classLanguage : (filenameLanguage ?? classLanguage)
  const { label, language } = normalizeCodeLanguage(languageInput)

  return (
    <CodeBlock code={codeText} language={language} showLineNumbers={false}>
      <CodeBlockHeader>
        <CodeBlockTitle>
          <FileIcon className="size-4" />
          <CodeBlockFilename>{filename ?? label}</CodeBlockFilename>
        </CodeBlockTitle>
        <CodeBlockActions>
          <CodeBlockLanguageSelector defaultValue={language}>
            <CodeBlockLanguageSelectorTrigger>
              <CodeBlockLanguageSelectorValue />
            </CodeBlockLanguageSelectorTrigger>
            <CodeBlockLanguageSelectorContent>
              <CodeBlockLanguageSelectorItem value={language}>{label}</CodeBlockLanguageSelectorItem>
            </CodeBlockLanguageSelectorContent>
          </CodeBlockLanguageSelector>
          <CodeBlockCopyButton size="iconSm" />
        </CodeBlockActions>
      </CodeBlockHeader>
    </CodeBlock>
  )
}

function codeLanguageFromClassName(className?: string) {
  return className?.match(/(?:^|\s)language-([^\s]+)/)?.[1]?.toLowerCase()
}

function codeLanguageFromFilename(filename: string) {
  return filename.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase()
}

function codeFilenameFromMeta(meta?: string) {
  if (!meta) return undefined

  const namedFilename = meta.match(/\b(?:filename|file|title)=["']([^"']+)["']/)?.[1]
  if (namedFilename) return namedFilename

  return meta.match(/(?:^|\s)([^\s"'{}[\]]+\.[A-Za-z0-9][\w-]*)/)?.[1]
}

function normalizeCodeLanguage(language?: string): { language: CodeBlockLanguage; label: string } {
  if (!language || plainTextLanguages.has(language)) {
    return { label: "Text", language: "text" }
  }

  const languageInfo = codeLanguageInfoByAlias.get(language)
  if (languageInfo) {
    return { label: languageInfo.name, language: languageInfo.id }
  }

  return { label: humanizeCodeLanguage(language), language: "text" }
}

function humanizeCodeLanguage(language: string) {
  return language
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

function textFromReactNode(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(textFromReactNode).join("")
  if (isValidElement<{ children?: ReactNode }>(node)) return textFromReactNode(node.props.children)

  return ""
}

function trimTrailingNewlines(value: string) {
  return value.replace(/\n+$/, "")
}

export const MessageResponse = memo(
  ({ className, components, ...props }: MessageResponseProps) => {
    const mergedComponents = useMemo(() => ({ ...defaultMarkdownComponents, ...components }), [components])

    return (
      <Streamdown
        className={cn(
          "size-full [&_ol]:list-outside [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-outside [&_ul]:list-disc [&_ul]:pl-6 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
          className
        )}
        components={mergedComponents}
        plugins={streamdownPlugins}
        {...props}
      />
    )
  },
  (prevProps, nextProps) => prevProps.children === nextProps.children && nextProps.isAnimating === prevProps.isAnimating
)

MessageResponse.displayName = "MessageResponse"

export type MessageToolbarProps = ComponentProps<"div">

export const MessageToolbar = ({ className, children, ...props }: MessageToolbarProps) => (
  <div className={cn("mt-4 flex w-full items-center justify-between gap-4", className)} {...props}>
    {children}
  </div>
)
